import React, { useState } from 'react';
import { HardDrive, Plus, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardBody, Button } from './ui';
import BuyStorageModal from './BuyStorageModal';

// StorageUsageCard
// ─────────────────────────────────────────────────────────────────────
// Customer-facing storage status card. Shows the firm admin / partner
// exactly:
//   * how much storage they're using
//   * what plan tier they're on (base + any purchased add-on)
//   * how much is left
//   * a clear CTA when they need more
//
// Renders nothing if storageInfo is null (no cap configured → either
// unlimited enterprise plan, or migration 143 hasn't been applied).
// Renders unlimited messaging when limit_mb is explicitly null.
//
// Props:
//   storageInfo  — the shape returned by getMyFirmStorageInfo()
//                  { used_mb, limit_mb, addon_mb, available_mb,
//                    is_full, is_near_full } | null
//   canPurchase  — whether to show the "Buy more storage" CTA (only
//                  Admins / Partners should see it)

function formatBytes(mb) {
  if (mb == null) return '—';
  if (mb >= 1024) {
    const gb = mb / 1024;
    return gb >= 100
      ? `${gb.toFixed(0)} GB`
      : `${gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
}

export default function StorageUsageCard({ storageInfo, canPurchase = false }) {
  const [showBuy, setShowBuy] = useState(false);

  if (!storageInfo) return null;

  const { used_mb, limit_mb, addon_mb, available_mb, is_full, is_near_full } = storageInfo;

  // Unlimited plan path — show a simple "all good" card without the
  // bar / percentage so we don't draw a 0% bar against an infinite
  // denominator.
  if (limit_mb == null) {
    return (
      <Card>
        <CardBody>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
              <HardDrive size={18} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Storage
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Your plan includes unlimited storage. {formatBytes(used_mb)} currently in use.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  const pct = limit_mb > 0 ? Math.min(100, Math.round((used_mb / limit_mb) * 100)) : 0;

  // Color the bar + icon to match the trigger state. is_full and
  // is_near_full come from the SQL helper so the threshold (90%)
  // matches between server and UI.
  const barColor    = is_full     ? 'bg-red-500'
                    : is_near_full ? 'bg-amber-500'
                    :                'bg-emerald-500';
  const iconBg      = is_full      ? 'bg-red-100    dark:bg-red-900/30'
                    : is_near_full ? 'bg-amber-100  dark:bg-amber-900/30'
                    :                'bg-indigo-100 dark:bg-indigo-900/30';
  const iconColor   = is_full      ? 'text-red-600    dark:text-red-400'
                    : is_near_full ? 'text-amber-600  dark:text-amber-400'
                    :                'text-indigo-600 dark:text-indigo-400';
  const Icon        = is_full ? AlertTriangle : HardDrive;

  // Base / add-on breakdown line shown under the bar.
  const baseMb = limit_mb - (addon_mb || 0);

  return (
    <>
      <Card>
        <CardBody>
          <div className="flex items-start gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
              <Icon size={18} className={iconColor} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Storage
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {is_full
                  ? 'Storage is full — uploads are blocked until you free space or add more.'
                  : is_near_full
                  ? 'Running low on storage.'
                  : `${formatBytes(available_mb)} available`}
              </p>
            </div>
          </div>

          {/* Bar */}
          <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className={`h-full ${barColor} transition-all`}
              style={{ width: `${pct}%` }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label={`Storage usage: ${pct}%`}
            />
          </div>

          {/* Numbers */}
          <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
            <div>
              <p className="text-slate-500 dark:text-slate-400">Used</p>
              <p className="font-semibold text-slate-900 dark:text-white">{formatBytes(used_mb)}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Total</p>
              <p className="font-semibold text-slate-900 dark:text-white">{formatBytes(limit_mb)}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400">Available</p>
              <p className={`font-semibold ${
                is_full ? 'text-red-600 dark:text-red-400'
                : is_near_full ? 'text-amber-700 dark:text-amber-400'
                : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {formatBytes(available_mb)}
              </p>
            </div>
          </div>

          {/* Breakdown — only shown if there's actually an add-on */}
          {addon_mb > 0 && (
            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              <CheckCircle2 size={11} className="inline mr-1 text-emerald-500" />
              Base plan: {formatBytes(baseMb)} · Add-on purchased: {formatBytes(addon_mb)}
            </p>
          )}

          {/* CTA */}
          {canPurchase && (
            <div className="mt-4 flex items-center justify-between gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {is_near_full || is_full
                  ? 'Add more storage to keep uploading documents.'
                  : 'Need more space later? Top up any time.'}
              </p>
              <Button
                icon={Plus}
                onClick={() => setShowBuy(true)}
                variant={is_full ? 'primary' : 'secondary'}
              >
                Buy more storage
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {showBuy && (
        <BuyStorageModal
          open={showBuy}
          onClose={() => setShowBuy(false)}
          currentAddonMb={addon_mb || 0}
        />
      )}
    </>
  );
}
